import { AtomicContext } from '@contexts/coveo/AtomicContext';
import {
  CategoryFacet,
  CategoryFacetState,
  buildCategoryFacet,
} from '@coveo/atomic-react';
import type { CategoryMetadata, CoveoFacetError } from '@lib/types/category';
import { buildCategoryHierarchy } from '@lib/util/buildCategoryHierarchy';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { TreeNodeData } from 'ui-components';

export type UseCategorySelectorStateReturn = {
  treeData: TreeNodeData<CategoryMetadata>[];
  selectedCategoryPath: string[];
  isLoading: boolean;
  error: CoveoFacetError | null;
  selectCategory: (categoryPath: string[]) => void;
  clearCategory: () => void;
  categoryController: CategoryFacet | null;
};

/**
 * Hook to manage category selector state using Coveo Headless.
 * Returns tree data only -- filtering is handled via useCategoryFilter (advancedQuery).
 */
export function useCategorySelectorState(
  context: AtomicContext
): UseCategorySelectorStateReturn {
  const { searchEngine } = useContext(context);
  const [error, setError] = useState<CoveoFacetError | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [categoryController, setCategoryController] =
    useState<CategoryFacet | null>(null);
  const [categoryState, setCategoryState] = useState<CategoryFacetState | null>(
    null
  );

  const treeData = useMemo(() => {
    const treeValues = categoryState?.valuesAsTrees ?? [];
    if (treeValues.length === 0) {
      return [];
    }

    try {
      return buildCategoryHierarchy(treeValues, ' > ');
    } catch (err) {
      setError({
        code: 'STATE_ERROR',
        message: 'Failed to build category tree',
        originalError: err instanceof Error ? err : undefined,
      });
      return [];
    }
  }, [categoryState?.valuesAsTrees]);

  useEffect(() => {
    if (!searchEngine) {
      return;
    }

    setIsLoading(true);

    try {
      const controller = buildCategoryFacet(searchEngine, {
        options: {
          field: 'ec_category',
          delimitingCharacter: ',',
          numberOfValues: 150,
          filterFacetCount: false,
        },
      });

      setCategoryController(controller);
      setCategoryState(controller.state);
      setError(null);
    } catch (err) {
      setError({
        code: 'INITIALIZATION_ERROR',
        message: 'Failed to initialize category facet',
        originalError: err instanceof Error ? err : undefined,
      });
      setIsLoading(false);
    }
  }, [searchEngine]);

  useEffect(() => {
    if (!categoryController) {
      return;
    }

    try {
      const unsubscribe = categoryController.subscribe(() => {
        setCategoryState(categoryController.state);
        setIsLoading(false);
      });

      return unsubscribe;
    } catch (err) {
      setError({
        code: 'SUBSCRIPTION_ERROR',
        message: 'Failed to subscribe to category facet state',
        originalError: err instanceof Error ? err : undefined,
      });
    }
  }, [categoryController]);

  const selectedCategoryPath = useMemo(() => {
    const ancestry = categoryState?.selectedValueAncestry ?? [];
    return ancestry.map((item) => item.value);
  }, [categoryState?.selectedValueAncestry]);

  /**
   * Updates the facet's internal selection state only (for UI highlighting).
   * Does NOT filter search results -- use useCategoryFilter.applyFilter for that.
   */
  const selectCategory = useCallback(
    (categoryPath: string[]) => {
      if (!categoryController || categoryPath.length === 0) {
        return;
      }

      try {
        categoryController.deselectAll();
        categoryController.toggleSelect({
          path: categoryPath,
          value: categoryPath[categoryPath.length - 1],
          numberOfResults: 0,
          children: [],
          moreValuesAvailable: false,
          state: 'idle',
          isLeafValue: true,
        });
      } catch (err) {
        setError({
          code: 'STATE_ERROR',
          message: 'Failed to select category',
          originalError: err instanceof Error ? err : undefined,
        });
      }
    },
    [categoryController]
  );

  const clearCategory = useCallback(() => {
    if (!categoryController) {
      return;
    }

    try {
      categoryController.deselectAll();
    } catch (err) {
      setError({
        code: 'STATE_ERROR',
        message: 'Failed to clear category',
        originalError: err instanceof Error ? err : undefined,
      });
    }
  }, [categoryController]);

  return {
    treeData,
    selectedCategoryPath,
    isLoading,
    error,
    selectCategory,
    clearCategory,
    categoryController,
  };
}
